/**
 * Athos configuration utilities
 * Provides site ID, origin and domain configuration for the Athos v1 discovery API.
 */

import { globalState } from '@/components/helpers/utils'

export function getAthosSiteID(category = 'products'): string {
  return globalState.siteIds[category]
}

/**
 * "Show in-stock products only" theme setting (`athos_instock_only`).
 *
 * Consumers add `bgfilter.ss_available=1` to product-grid requests when this is
 * on. Athos cross-matches filters at the variant level, so the one constraint
 * both drops fully sold-out products and makes the `size` facet counts reflect
 * the sizes actually in stock.
 *
 * Requires an explicit `true`, so activation is tied to the content deploy
 * rather than the code deploy. `config/*.json` is excluded from `--build`, so
 * until the schema lands Liquid renders this as `null` and the constraint stays
 * off. That ordering matters: the constraint hides sold-out products and moves
 * PLP counts noticeably, and it should not go live before the merchant-facing
 * toggle exists to switch it back off. Once `--content` deploys, the schema
 * default (`true`) applies.
 */
export function isAthosInstockOnly(): boolean {
  return globalState.athosInstockOnly === true
}

/**
 * Standard discovery API origin (search, category, autocomplete, suggest, trending, meta).
 * @param {string} siteID
 * @returns {string}
 */
export function getApiOrigin(siteID: string): string {
  return `https://${siteID}.a.athoscommerce.net/v1`
}

/**
 * Personalization (P13N) origin — Athos serves recommendations from a dedicated
 * cluster, distinct from the standard discovery origin.
 * @param {string} siteID
 * @returns {string}
 */
export function getPersonalizationOrigin(siteID: string): string {
  return `https://${siteID}.a.p13n.athoscommerce.net/v1`
}

/**
 * Beacon 2.0 analytics origin.
 * @returns {string}
 */
export function getBeaconOrigin(): string {
  return 'https://analytics.athoscommerce.net/beacon/v2'
}

/**
 * The `domain` request param must always carry the actual current page URL —
 * it drives merchandising/landing-page triggers, not index selection (that is
 * the site ID's job). Feed URLs (`?view=ss-feed`) belong only in the AMC feed
 * configuration, never in API requests.
 */
export function getAthosDomain(): string {
  return window.location.href
}
